import { useInterviewStore } from '@/stores/useInterviewStore'
import { FileText } from 'lucide-react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Typography from '@tiptap/extension-typography'
import { Markdown } from 'tiptap-markdown'
import { useEffect, useMemo, useRef } from 'react'
import { Addition, Deletion, Note, parseAnnotations } from '@/components/editor/AnnotationExtension'

export function ScratchpadDocumentContent() {
  const {
    scratchpadContent,
    scratchpadDisplayContent,
    scratchpadDocumentStreaming,
    scratchpadStreamingLineCount,
    buildMode,
    workflowStatus,
  } = useInterviewStore()

  // Document display: Use transient displayContent if available, fall back to persisted content
  // - scratchpadDisplayContent: Set during streaming (shows diff annotations)
  // - scratchpadContent: Persisted content
  const displayContent = scratchpadDisplayContent ?? scratchpadContent

  const editorContainerRef = useRef<HTMLDivElement>(null)
  const prevContentRef = useRef<string>('')

  // Parse annotation markers to HTML before passing to TipTap
  const parsedContent = useMemo(() => {
    if (!displayContent) return ''
    return parseAnnotations(displayContent)
  }, [displayContent])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
      }),
      Markdown.configure({
        html: true,  // Allow HTML tags within markdown (for annotation marks)
      }),
      Link.configure({
        openOnClick: false,
      }),
      Typography,
      Addition,
      Deletion,
      Note,
    ],
    content: parsedContent || '',
    editable: false, // Scratchpad is always read-only (agent's working notes)
    editorProps: {
      attributes: {
        class: 'prose-zinsser outline-none',
      },
    },
    immediatelyRender: false,
  }, [])

  // Update editor content and trigger animations when content changes
  useEffect(() => {
    if (!editor) return

    const contentChanged = prevContentRef.current !== parsedContent
    prevContentRef.current = parsedContent

    editor.commands.setContent(parsedContent || '', { emitUpdate: false })

    // Trigger animations on new annotations after content update
    if (contentChanged && parsedContent && editorContainerRef.current) {
      requestAnimationFrame(() => {
        const container = editorContainerRef.current
        if (!container) return

        const additions = container.querySelectorAll('.annotation-addition')
        const deletions = container.querySelectorAll('.annotation-deletion')

        additions.forEach(el => {
          el.classList.remove('annotation-animate')
          void (el as HTMLElement).offsetWidth
          el.classList.add('annotation-animate')
        })
        deletions.forEach(el => {
          el.classList.remove('annotation-animate')
          void (el as HTMLElement).offsetWidth
          el.classList.add('annotation-animate')
        })
      })
    }
  }, [editor, parsedContent])

  // Highlight last block during streaming for progress indication
  useEffect(() => {
    const container = editorContainerRef.current
    if (!container) return

    // Get all block-level elements (paragraphs, headings, list items)
    const blocks = container.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li')

    // If not streaming, remove all highlights
    if (!scratchpadDocumentStreaming) {
      blocks.forEach(el => el.classList.remove('streaming-line-highlight'))
      return
    }

    // Remove highlight from all blocks first
    blocks.forEach(el => el.classList.remove('streaming-line-highlight'))

    // Highlight the LAST block element (where new content is being added)
    const lastBlock = blocks[blocks.length - 1]
    if (lastBlock) {
      lastBlock.classList.add('streaming-line-highlight')
      // Auto-scroll to keep the last block visible
      lastBlock.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }

    // Cleanup: remove highlights when effect re-runs or unmounts
    return () => {
      blocks.forEach(el => el.classList.remove('streaming-line-highlight'))
    }
  }, [scratchpadDocumentStreaming, scratchpadStreamingLineCount])

  return (
    <div className="h-full flex flex-col">
      {/* Editing indicator - shows at top when streaming */}
      {scratchpadDocumentStreaming && (
        <div className="flex-shrink-0 px-4 py-2 border-b border-amber-500/30 bg-amber-500/10">
          <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
            <span className="inline-block w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
            <span className="font-medium">Updating notes...</span>
          </div>
        </div>
      )}

      {/* Document content */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {displayContent ? (
          <div className="relative" ref={editorContainerRef}>
            <EditorContent editor={editor} />
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-center">
            <div>
              <FileText className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-sm text-muted-foreground">
                Agent's working notes will appear here
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                {buildMode === 'foolscap'
                  ? 'Notes help refine the Foolscap structure'
                  : 'Enter Foolscap mode to generate notes'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Copy button - show when not actively streaming */}
      {scratchpadContent && workflowStatus !== 'streaming' && (
        <div className="flex-shrink-0 px-4 py-3 border-t border-border">
          <button
            onClick={() => {
              navigator.clipboard.writeText(scratchpadContent)
            }}
            className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors text-center py-1"
          >
            Click to copy Scratchpad
          </button>
        </div>
      )}
    </div>
  )
}
